import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading
    const [error, setError] = useState("");

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.token) localStorage.setItem("verslan_token", data.token);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { email, password });
            if (data.token) localStorage.setItem("verslan_token", data.token);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("verslan_token");
        setUser(null);
    };

    // Google OAuth — kicks off the browser redirect. Backend returns Google's
    // consent URL for us to jump to; on return, /auth/google/callback route
    // finishes the exchange and hands us the JWT.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH.
    const startGoogleLogin = async () => {
        setError("");
        try {
            const redirectUri = window.location.origin + "/auth/google/callback";
            const { data } = await api.get("/auth/google/start", { params: { redirect_uri: redirectUri } });
            sessionStorage.setItem("verslan_google_redirect_uri", redirectUri);
            window.location.href = data.auth_url;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    // Called by the /auth/google/callback page after Google bounces the
    // browser back with ?code=…&state=… in the URL.
    const finishGoogleLogin = async (code, state) => {
        setError("");
        try {
            const redirectUri = sessionStorage.getItem("verslan_google_redirect_uri")
                || (window.location.origin + "/auth/google/callback");
            const { data } = await api.post("/auth/google/callback", {
                code, state, redirect_uri: redirectUri,
            });
            sessionStorage.removeItem("verslan_google_redirect_uri");
            if (data.token) localStorage.setItem("verslan_token", data.token);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    return (
        <AuthContext.Provider value={{
            user, error, login, register, logout, refresh: fetchMe,
            startGoogleLogin, finishGoogleLogin,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
